<script setup>
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

// Read-only preview of a form template, drawn as the published form would
// look like: banner, questions (label and an empty field of their type) and
// submit button. When only part of the questions are shown, the form fades
// out with the count of the other ones instead of the submit button.
defineProps({
  questions: {
    type: Array,
    required: true
  },
  // Taller banner and more spacing, for the full preview.
  large: {
    type: Boolean,
    default: false
  },
  // Number of questions not shown.
  more: {
    type: Number,
    default: 0
  }
});

const emit = defineEmits(['more']);

// Icon shown in the single line fields.
const fieldIcons = {
  email: 'pi pi-at',
  tel: 'pi pi-phone',
  number: 'pi pi-hashtag',
  date: 'pi pi-calendar',
  webform_time: 'pi pi-clock'
};
</script>

<template>
  <div class="flex flex-col rounded border border-surface-200 bg-surface-0 overflow-hidden pointer-events-none select-none">
    <div class="bg-primary-200" :class="large ? 'h-20' : 'h-8'"></div>
    <div class="flex flex-col flex-1 gap-3" :class="large ? 'p-5' : 'p-3'">
      <div class="relative flex flex-col gap-3">
        <div v-for="(question, index) in questions" :key="index" class="flex flex-col gap-1">
          <span class="text-xs font-medium">{{ question.title }}<span v-if="question.required" class="text-red-500"> *</span></span>

          <div v-if="question.type === 'textarea'" class="h-12 rounded border border-surface-300 bg-surface-0"></div>

          <div v-else-if="question.type === 'radios' || question.type === 'checkboxes'" class="flex flex-col gap-1">
            <div v-for="(option, key) in question.options" :key="key" class="flex items-center gap-2 text-xs text-muted-color">
              <span class="w-3 h-3 shrink-0 border border-surface-400"
                :class="question.type === 'radios' ? 'rounded-full' : 'rounded-sm'"></span>
              {{ option }}
            </div>
          </div>

          <div v-else-if="question.type === 'select'"
            class="flex items-center justify-between h-7 px-2 rounded border border-surface-300 bg-surface-0 text-xs text-muted-color">
            {{ t('common.placeholders.select') }}<i class="pi pi-chevron-down text-xs"></i>
          </div>

          <div v-else-if="question.type === 'webform_rating'" class="flex gap-1 text-surface-300">
            <i v-for="n in 5" :key="n" class="pi pi-star"></i>
          </div>

          <div v-else-if="question.type === 'managed_file'"
            class="flex items-center justify-center h-10 rounded border border-dashed border-surface-300 bg-surface-0 text-muted-color">
            <i class="pi pi-upload"></i>
          </div>

          <div v-else class="flex items-center justify-end h-7 px-2 rounded border border-surface-300 bg-surface-0 text-muted-color">
            <i v-if="fieldIcons[question.type]" class="text-xs" :class="fieldIcons[question.type]"></i>
          </div>
        </div>
        <div v-if="more" class="absolute inset-x-0 bottom-0 h-12"
          style="background: linear-gradient(to top, var(--p-surface-0), transparent)"></div>
      </div>
      <!-- Pushed to the bottom, aligned across cards of the same height. -->
      <button v-if="more" type="button"
        class="self-center mt-auto text-xs text-primary hover:underline pointer-events-auto cursor-pointer"
        @click.stop="emit('more')">{{ t('webform.start.more', more) }}</button>
      <span v-else class="self-start mt-auto px-3 py-1 rounded bg-primary text-primary-contrast text-xs">{{
        t('common.labels.send') }}</span>
    </div>
  </div>
</template>
